/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import LoggerFactory from '../logger/LoggerFactory';
import {ILogger} from '../logger/LoggerInterface';
import Subject from '../rx/Subject';
import FeatureEnablement from '../environment/FeatureEnablement';
import IPeerConnection from './IPeerConnection';
import IPeerConnectionFactory from './IPeerConnectionFactory';
import VanillaPeerConnectionFactory from './VanillaPeerConnectionFactory';

export interface IPeerConnectionOfferInit {
  audioTransceivers?: RTCRtpTransceiver[];
  videoTransceivers?: RTCRtpTransceiver[];
  peerConnection: IPeerConnection;
  localOffer: RTCSessionDescriptionInit;
}

export enum EncodedInsertableStreams {
  Disabled,
  Enabled
}

export const defaultTrackKinds: ('audio' | 'video')[] = ['audio', 'video'];

export default class PeerConnectionService {
  private static _logger: ILogger = LoggerFactory.getLogger('PeerConnectionService');
  private static readonly _peerConnectionFactory: Subject<IPeerConnectionFactory> = new Subject<IPeerConnectionFactory>(new VanillaPeerConnectionFactory());
  private static _cached: {
    direction?: RTCRtpTransceiverDirection;
    peerConnection: Promise<IPeerConnectionOfferInit>;
  } | null;

  static get peerConnectionFactory(): Subject<IPeerConnectionFactory> {
    return PeerConnectionService._peerConnectionFactory;
  }

  static initiateInitialPrecaching(): void {
    if (FeatureEnablement.isPrecachingEnabled) {
      const ignored = PeerConnectionService.precacheCreatePeerConnectionOffer()
        .catch(e => PeerConnectionService._logger.error('Failed to pre-cache peer connection offer', e));
    }
  }

  static async precacheCreatePeerConnectionOffer(): Promise<{ peerConnection: Promise<IPeerConnectionOfferInit | void>; direction: RTCRtpTransceiverDirection }> {
    return PeerConnectionService._cached = {
      direction: 'recvonly',
      peerConnection: PeerConnectionService.createPeerConnectionOffer()
        .catch(e => {
          PeerConnectionService._cached = null;

          throw e;
        })
    };
  }

  static async createPeerConnectionOffer(direction: RTCRtpTransceiverDirection = 'recvonly', trackKinds = defaultTrackKinds, encodedInsertableStreams: EncodedInsertableStreams = EncodedInsertableStreams.Disabled): Promise<IPeerConnectionOfferInit> {
    if (PeerConnectionService._cached && PeerConnectionService._cached.direction === direction && encodedInsertableStreams === EncodedInsertableStreams.Disabled && this.isTracksKindsEquivalentToDefault(trackKinds)) {
      const value = PeerConnectionService._cached.peerConnection;

      PeerConnectionService._cached = null;

      return value;
    }

    const configuration: RTCConfiguration | undefined = FeatureEnablement.isEncodedInsertableStreamsSupported ? {encodedInsertableStreams: encodedInsertableStreams === EncodedInsertableStreams.Enabled} : undefined;

    return PeerConnectionService._peerConnectionFactory.value.createPeerConnection(configuration)
      .then(async peerConnection => {
        const supportsAddTransceiver = peerConnection.supportsAddTransceiver;
        const audioTransceivers: RTCRtpTransceiver[] = [];
        const videoTransceivers: RTCRtpTransceiver[] = [];

        if (supportsAddTransceiver) {
          trackKinds.forEach(kind => {
            if (kind === 'audio') {
              audioTransceivers.push(peerConnection.addTransceiver(kind, {direction}));

              return;
            }

            if (kind === 'video') {
              videoTransceivers.push(peerConnection.addTransceiver(kind, {direction}));

              return;
            }
          });

          return {
            audioTransceivers,
            videoTransceivers,
            peerConnection: peerConnection,
            localOffer: await peerConnection.createOffer(undefined)
          };
        }

        const options = direction === 'recvonly' ? {
          offerToReceiveAudio: true,
          offerToReceiveVideo: true
        } : {};

        return {
          peerConnection: peerConnection,
          localOffer: await peerConnection.createOffer(options)
        };
      });
  }

  private static isTracksKindsEquivalentToDefault(tracksKinds: ('audio' | 'video')[]): boolean {
    if (tracksKinds.length !== defaultTrackKinds.length) {
      return false;
    }

    const countTrackKinds = (array: ('audio' | 'video')[]) => array.reduce((trackKindCount, kind) => {
      trackKindCount[kind] = (trackKindCount[kind] || 0) + 1;

      return trackKindCount;
    }, {
      audio: 0,
      video: 0
    });
    const countedTrackKinds = countTrackKinds(tracksKinds);
    const countedDefaultTrackKinds = countTrackKinds(defaultTrackKinds);

    return countedTrackKinds.audio === countedDefaultTrackKinds.audio && countedTrackKinds.video === countedDefaultTrackKinds.video;
  }

  private constructor() {
    throw new Error('PeerConnectionService is a static class that may not be instantiated');
  }
}

PeerConnectionService.initiateInitialPrecaching();